import { Injectable } from '@angular/core'
import { Observable, Observer } from 'rxjs'
import { AnonymousSubject } from 'rxjs/internal/Subject'
import { map } from 'rxjs/operators'
import { Message } from '../shared-components/common-interface'

@Injectable()
export class WebsocketService {
  private subject: AnonymousSubject<MessageEvent>
  private activeWs: WebSocket | null = null
  public messages$: Observable<Message>

  constructor() {
    this.initConnection()
  }

  public reconnect(): void {
    this.initConnection()
  }

  private initConnection(): void {
    // Explicitly close the current socket before opening a new one.
    // subject.unsubscribe() alone does not guarantee ws.close() is called
    // because create() opens the WebSocket eagerly, outside the Observable's
    // subscribe/teardown lifecycle.
    if (this.activeWs && this.activeWs.readyState !== WebSocket.CLOSED) {
      this.activeWs.close()
    }
    this.activeWs = null
    this.messages$ = this.buildMessages()
  }

  private buildSocketUrl(): string {
    const token = sessionStorage.getItem('api_token')
    const tenantId = sessionStorage.getItem('tenant_id')
    const baseURL = JSON.parse(localStorage.getItem('baseUrl')).api.url
    const socketURL = baseURL.replace(/\/\/|.+\/\//, 'wss://')
    return (
      socketURL +
      'Generic/notificationCount?token=' +
      token +
      '&tenant_id=' +
      tenantId
    )
  }

  private buildMessages(): Observable<Message> {
    return this.connect(this.buildSocketUrl()).pipe(
      map((response: MessageEvent): Message => response.data)
    )
  }

  public connect(url): AnonymousSubject<MessageEvent> {
    if (!this.subject) {
      this.subject = this.create(url)
    } else {
      this.subject.unsubscribe()
      this.subject = this.create(url)
    }
    return this.subject
  }

  public create(url): AnonymousSubject<MessageEvent> {
    const ws = new WebSocket(url)
    this.activeWs = ws
    const observable = new Observable((obs: Observer<MessageEvent>) => {
      ws.onmessage = obs.next.bind(obs)
      ws.onerror = obs.error.bind(obs)
      ws.onclose = () => {
        // Guard: if reconnect() has already replaced this socket, do not
        // start the retry loop — it would spin indefinitely against a stale
        // observer and leave orphaned WebSockets open.
        if (this.activeWs !== ws) {
          return
        }
        setTimeout(() => {
          if (this.activeWs !== ws) {
            return
          }
          const openSocket = (
            wsURL: string,
            waitTimer: number,
            waitSeed: number,
            multiplier: number
          ) => {
            const retryWs = new WebSocket(wsURL)
            this.activeWs = retryWs
            retryWs.onopen = () => {
              waitTimer = waitSeed // reset the waitTimer if the connection is made
              retryWs.onmessage = obs.next.bind(obs)
              retryWs.onclose = () => {
                // Only recurse if this retry socket is still the active one
                if (this.activeWs === retryWs) {
                  openSocket(retryWs.url, waitTimer, waitSeed, multiplier)
                }
              }
            }
            retryWs.onerror = () => {
              //increase the wait timer if not connected, but stop at a max of 2n-1 the check time
              //   if(waitTimer < 60000) waitTimer = waitTimer * multiplier;
              //   setTimeout(()=>{openSocket(ws.url, waitTimer, waitSeed, multiplier)}, waitTimer);
            }
          }
          openSocket(this.buildSocketUrl(), 5000, 5000, 2)
        }, 2000)
      }
      // Teardown: called when the Observable is unsubscribed, closing the socket
      return () => {
        if (ws.readyState !== WebSocket.CLOSED) {
          ws.close()
        }
      }
    })
    const observer = {
      error: null,
      complete: null,
      next: (data: any) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify(data))
        }
      },
    }
    return new AnonymousSubject<MessageEvent>(observer, observable)
  }
}
